<template>
  <div class="min-h-screen">
    <!-- Navigation -->
    <nav class="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16">
          <!-- Logo -->
          <div class="flex items-center">
             <div class=" mr-2 w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden">
                <img src="@/assets/EXACTFLO_LOGO_BLACKNOWORD.png" alt="Logo" class="w-full h-full object-contain" />
            </div>
            <router-link
              to="/"
              class="text-2xl font-bold bg-gradient-to-r from-purple-500 to-purple-700 bg-clip-text text-transparent"
            >
            
              ExactFlo Suite
            </router-link>
          </div>

          <!-- Desktop Menu -->
          <!-- <div class="hidden md:block">
            <div class="ml-10 flex items-baseline space-x-8">
              <a href="#features" class="text-gray-700 hover:text-purple-600 transition-colors font-medium">
                Features
              </a>
              <a href="#pricing" class="text-gray-700 hover:text-purple-600 transition-colors font-medium">
                Pricing
              </a>
              <a href="#testimonials" class="text-gray-700 hover:text-purple-600 transition-colors font-medium">
                Testimonials
              </a>
            </div>
          </div> -->

          <!-- Desktop Auth Buttons -->
          <div class="hidden md:flex items-center space-x-4">
            <router-link to="/login">
              <button class="px-4 py-2 rounded-lg font-semibold text-gray-700 border border-gray-300 hover:bg-gray-100">
                Login
              </button>
            </router-link>
            <router-link to="/signup">
              <button
                class="px-4 py-2 rounded-lg font-semibold text-white bg-gradient-to-r from-purple-500 to-purple-700 hover:opacity-90"
              >
                Sign Up
              </button>
            </router-link>
          </div>

          <!-- Mobile Menu Toggle -->
          <div class="md:hidden">
            <button
              @click="isMenuOpen = !isMenuOpen"
              class="text-gray-700 hover:text-purple-600 transition-colors"
            >
              <Menu v-if="!isMenuOpen" class="h-6 w-6" />
              <X v-else class="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      <!-- Mobile Menu Dropdown -->
      <div v-if="isMenuOpen" class="md:hidden">
        <div class="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t border-gray-200">
          <!-- <a href="#features" class="block px-3 py-2 text-gray-700 hover:text-purple-600 transition-colors">
            Features
          </a>
          <a href="#pricing" class="block px-3 py-2 text-gray-700 hover:text-purple-600 transition-colors">
            Pricing
          </a>
          <a href="#testimonials" class="block px-3 py-2 text-gray-700 hover:text-purple-600 transition-colors">
            Testimonials
          </a> -->
          <div class="px-3 py-2 space-y-2">
            <router-link to="/login" class="block">
              <button
                class="w-full text-left px-4 py-2 rounded-lg font-semibold border border-gray-300 text-gray-700 hover:bg-gray-100"
              >
                Login
              </button>
            </router-link>
            <router-link to="/signup" class="block">
              <button
                class="w-full text-left px-4 py-2 rounded-lg font-semibold text-white bg-gradient-to-r from-purple-500 to-purple-700 hover:opacity-90"
              >
                Sign Up
              </button>
            </router-link>
          </div>
        </div>
      </div>
    </nav>

    <!-- Hero Section -->
    <section class="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div class="absolute inset-0 z-0">
        <img
          :src="heroImage"
          alt="Business automation platform"
          class="w-full h-full object-cover opacity-20"
        />
        <div class="absolute inset-0 bg-gradient-to-r from-purple-500/30 to-purple-700/30"></div>
      </div>

      <div class="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h1 class="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight drop-shadow-md">
          All-in-One Business
          <span class="block bg-gradient-to-r from-purple-400 to-purple-700 bg-clip-text text-transparent">
            Automation Platform
          </span>
        </h1>
        <p class="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed">
          Streamline your entire business with our comprehensive platform. From CRM to AI-powered marketing,
          task management - everything you need in one place.
        </p>

        <div class="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <router-link to="/signup">
            <button
              class="px-6 py-3 rounded-lg text-white font-semibold bg-gradient-to-r from-purple-500 to-purple-700 hover:opacity-90 flex items-center"
            >
              Start Free Trial
              <ArrowRight class="ml-2 h-5 w-5" />
            </button>
          </router-link>
          <a 
            href="https://wa.me/+6582266460" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <button
              class="px-6 py-3 rounded-lg border border-white/50 text-white hover:bg-white hover:text-purple-600 transition"
            >
              <Phone class="mr-2 h-5 w-5 inline-block" />
              Contact Sales
            </button>
          </a>
        </div>

        <p class="text-sm text-gray-300 mt-6">
          ✨ No credit card required • 14-day free trial • Cancel anytime
        </p>
      </div>
    </section>

    <!-- Features Section -->
    <section id="features" class="py-20 bg-gray-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-4xl md:text-5xl font-bold mb-6">Everything Your Business Needs</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Our platform combines essential business tools in one place to help you grow.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div
            v-for="(feature, index) in features"
            :key="index"
            class="bg-white p-8 rounded-xl shadow hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
          >
            <component :is="feature.icon" class="h-8 w-8 text-purple-600 mb-4" />
            <h3 class="text-xl font-semibold mb-3">{{ feature.title }}</h3>
            <p class="text-gray-600 leading-relaxed">{{ feature.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="bg-gray-900 text-white py-12">
      <div class="max-w-7xl mx-auto px-4 text-center">
        <p class="text-sm text-gray-400">© 2025 Exactflo by The Network Tap. All rights reserved.</p>
      </div>
    </footer>
  </div>
</template>

<script>
import {
  Menu,
  X,
  ArrowRight,
  Phone,
  Users,
  Lightbulb,
  Calendar,
  CheckSquare,
  FileText,
  Package,
  UserCheck,
  CreditCard,
  UserCog,
  Briefcase,
  Check
} from "lucide-vue-next";

import heroImage from "@/assets/hero-bg.jpg";

export default {
  name: "Index",
  components: {
    Menu,
    X,
    ArrowRight,
    Phone,
    Users,
    Lightbulb,
    Calendar,
    CheckSquare,
    FileText,
    Package,
    UserCheck,
    CreditCard,
    UserCog,
    Briefcase,
    Check
  },
  data() {
    return {
      isMenuOpen: false,
      heroImage,
      features: [
        { icon: Users, title: "CRM Management", description: "Track leads, manage contacts, and close more deals." },
        { icon: Lightbulb, title: "AI-Powered Ideas", description: "Generate marketing ideas and storyboards with AI." },
        { icon: Calendar, title: "Meeting Scheduler", description: "Coordinate teams and clients effortlessly." },
        { icon: CheckSquare, title: "Task Management", description: "Assign, monitor, and complete projects on time." },
        { icon: FileText, title: "Automated Quotations", description: "Generate professional quotes instantly." },
        { icon: Package, title: "Inventory Management", description: "Track products and stock levels in real-time." },
        { icon: UserCheck, title: "Leave Management", description: "Approve and track employee leave easily." },
        // { icon: CreditCard, title: "Claims & Payroll", description: "Automate payroll and claims processing." },
        { icon: UserCog, title: "HR Management", description: "Manage employee records and performance." },
        { icon: Briefcase, title: "Job Management", description: "Dispatch and track jobs efficiently." }
      ]
    };
  }
};
</script>
